export interface IUpdateProfileRequest {
  language: string;
  unitOfMeasurement: number;
  dataRegion?: string;
}

/**
 * MVP-18 Stage 2/3 — snapshot of the user's current Phase assignment. Mirrors
 * the SU `ICurrentPhaseInfo` shape from `api-contract.md` Section C so the
 * renewal banner can branch on `daysRemaining` / `isExpired` without pulling
 * in the super-user feature module.
 */
export interface ICurrentPhaseInfo {
  id: string;
  name: string;
  durationDays: number;
  startedAt: string | null;
  expiresAt: string | null;
  daysRemaining: number;
  isExpired: boolean;
}

/**
 * Test-phase metadata returned on `GET /api/users/me` and `GET /api/users/profile` (MVP-8).
 * Null when the user has no active phase (deactivated / production).
 */
export interface ITestPhase {
  startAt: string;
  endAt: string;
  daysRemaining: number;
  status: 'Active' | 'Expired' | 'Cancelled';
}

export interface IUserProfile extends IUpdateProfileRequest {
  labProvider?: string;
  populationRisk?: string;
  email: string;
  userId: string;
  medicalSpecialty: string;
  userRole?: number;
  dataRegion?: string; // read-only: reflects workspace DataRegion, not updatable via profile
  hasPassword?: boolean;
  /**
   * MVP-15: true when the backend flags this user as a Super User
   * (`User.IsSuperUser = true`). Drives the conditional primary-sidebar
   * "Super User" nav entry and the `superUserGuard` on `/su/*` routes.
   * Populated exclusively by `GET /api/users/profile` — no other endpoint
   * surfaces this flag.
   */
  isSuperUser?: boolean;
  testPhase?: ITestPhase | null;
  /**
   * Billing snapshot fields populated by `GET /api/users/profile` (MVP-9).
   * Used by the sales-contact modal to pre-fill the form. The same fields are
   * upserted on the server when the modal submits.
   */
  fullName?: string | null;
  billingDepartment?: string | null;
  billingStreet?: string | null;
  billingZip?: string | null;
  billingCity?: string | null;
  billingCountryId?: number | null;
  invoicingEmail?: string | null;
  /**
   * MVP-18 Stage 3 — phase assignment + expiry snapshot. Surfaced on
   * `GET /api/users/profile` once the backend profile endpoint is extended;
   * until then it stays undefined and the renewal banner silently hides.
   */
  currentPhase?: ICurrentPhaseInfo;
}
